/**
 * 새 티켓의 첫 걸음 — **프로젝트를 고르고, 유형을 고르면 대화가 시작된다**
 * (2026-09-16 사용자 지정).
 *
 * ⚠ **상태(칸)를 안 묻는다.** 유형이 정한다 — 이슈는 「이슈」 칸, 할 일은
 * 「Todo」 칸이 접수함이다. 접수하는 사람에게 「이걸 어느 칸에 둘까요」는
 * 대답할 수 없는 물음이고, 물으면 그 자리에서 접수가 멈춘다.
 *
 * ⚠ **작업 유형·우선순위·담당자도 여기 없다.** 기획이 아는 것은 티켓 요소
 * 열둘 중 두셋이다 — 나머지는 대화 결과로 AI 가 채우거나 비고, 사람이
 * 마지막 폼에서 고친다.
 *
 * **유형을 누르는 것이 곧 「시작」이다.** 단추를 따로 두면 고른 뒤 한 번 더
 * 눌러야 하는데, 이 화면에서 고를 것이 둘뿐이라 그 한 번이 그대로 마찰이다.
 * 되돌릴 길은 대화창의 「뒤로」다.
 *
 * ⚠ **유형은 라디오가 아니라 단추다.** 고른 상태가 남지 않고 누르는 즉시
 * 넘어가므로 「골라 둔 값」이 없다 — 라디오로 그리면 읽는 쪽에 없는 상태를
 * 알리게 된다. 프로젝트는 값이 남으므로 라디오 그대로다.
 */

import type { Project, TicketKind } from '../api';
import { KINDS } from '../columns';
import { Button } from '@/components/ui/button';

import { ChoiceGroup, Radio } from '../ui/Choice';
import { ModalActions } from '../ui/ModalActions';

export function CreateStart({
  projects,
  projectId,
  onProjectChange,
  onPick,
  onSwitchToForm,
  onCancel,
}: {
  /** **등록된 프로젝트만 온다.** 화면이 선택지를 지어내지 않는다. */
  projects: Project[];
  projectId: number;
  onProjectChange: (id: number) => void;
  /** 유형을 골랐다 — 대화 시작. */
  onPick: (kind: TicketKind) => void;
  /** 「직접 적기」 — 챗봇 없이 폼으로. */
  onSwitchToForm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex shrink-0 items-center border-b border-line px-s5 py-s3">
        <h2 className="m-0 text-lead font-bold text-ink">새 티켓</h2>
      </header>

      <dl className="m-0 min-h-0 flex-1 overflow-y-auto grid grid-cols-[68px_1fr] content-start items-start gap-x-s4 gap-y-s4 px-s5 py-s4">
        <dt className="pt-s2 text-meta text-ink-3">프로젝트</dt>
        <dd className="m-0 min-w-0">
          {/* 하나뿐이어도 그린다 — 어디에 내는지가 보여야 한다. */}
          <ChoiceGroup label="프로젝트">
            {projects.map((item) => (
              <Radio
                key={item.id}
                name="start-project"
                label={item.name}
                nullable={false}
                checked={projectId === item.id}
                onChange={() => onProjectChange(item.id)}
              >
                {item.name}
              </Radio>
            ))}
          </ChoiceGroup>
        </dd>

        <dt className="pt-s2 text-meta text-ink-3">유형</dt>
        <dd className="m-0 min-w-0">
          <div className="flex flex-wrap gap-s2" role="group" aria-label="유형">
            {KINDS.map((item) => (
              <Button
                key={item.id}
                type="button"
                variant="ghost"
                className="min-w-[96px] flex-1"
                onClick={() => onPick(item.id)}
              >
                {item.label}
              </Button>
            ))}
          </div>
        </dd>
      </dl>

      {/* **긍정 단추가 없는 화면이다** — 유형을 누르는 것이 곧 진행이라
          아래에는 빠져나가는 길만 둔다. 「직접 적기」는 챗봇을 건너뛰는
          대안이지 이 화면의 주 동작이 아니다(챗봇은 관문이 아니다). */}
      <ModalActions
        placement="frame"
        secondary={[{ label: '직접 적기', onClick: onSwitchToForm }]}
        cancel={{ label: '취소', onClick: onCancel }}
      />
    </div>
  );
}
